import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import React, { FormEventHandler, useCallback, useContext, useEffect, useRef, useState } from 'react'
import BusinessTravellerStepper from 'businessTraveller/components/stepper/BusinessTravellerStepGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import Divider from 'LuxKit/Divider'
import config from 'constants/config'
import SocialButtons from 'components/Account/AccountAccess/AccountAccessSocialButtons'
import { useMutation } from '@tanstack/react-query'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import styled from 'styled-components'
import { formToObject } from 'lib/forms/formToObject'
import ReCAPTCHA from 'react-google-recaptcha'
import handleReCAPTCHA from 'businessTraveller/utils/handleReCAPTCHA'
import { AuthAccountLoginResponse, login } from 'api/auth'
import {
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_CREDIT_ACCOUNT,
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO,
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS,
  BusinessTravellerBusinessOnboardingDispatchContext,
  BusinessTravellerBusinessOnboardingStateContext,
} from 'businessTraveller/contexts/BusinessTravellerBusinessOnboardingContexts'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { LUXURY_ESCAPES } from 'constants/brands'
import {
  BUSINESS_TRAVELLER_BUSINESS_SIGNUP_GOOGLE_LINK_ACCOUNT_CALLBACK_SOURCE,
  BUSINESS_TRAVELLER_BUSINESS_SIGNUP_GOOGLE_LINK_ACCOUNT_FAILURE_EVENT,
  BUSINESS_TRAVELLER_BUSINESS_SIGNUP_GOOGLE_LINK_ACCOUNT_SUCCESS_EVENT,
} from 'actions/businessTraveller/loginGoogle'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { API_CALL_SUCCESS } from 'actions/actionConstants'
import { USER_LOGIN } from 'actions/apiActionConstants'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'

const SignupContainer = styled(LayoutContainer)`
  background-color:  ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(24)};
  }
`

const LoginForm = styled.form`
  position: relative;
`

const stepStatuses: App.BusinessTraveller.BusinessSignupStepKeyStatuses = {
  validate_email: 'complete',
  account_details: 'active',
  create_password: 'pending',
}

interface Props {
  steps: App.BusinessTraveller.BusinessSignupSteps
}

function BusinessTravellerSignupLinkAccount(props: Props) {
  const { steps } = props
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)

  const { adminEmail } = useContext(BusinessTravellerBusinessOnboardingStateContext)
  const accountSetupDispatch = useContext(BusinessTravellerBusinessOnboardingDispatchContext)
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isDedicatedTenantRegistrationEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lebtDedicatedTenantRegistration)

  const formRef = useRef<HTMLFormElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const reCaptchaRef = useRef<ReCAPTCHA>(null)

  const isSocialMediaLoginEnabled = !!(config.GOOGLE_APP_ID)

  const continueWithCreditAccount = useCallback((account: App.AuthAccount, accessToken: string, businessTravellerAccount?: AuthAccountLoginResponse) => {
    if (!account.email || !account.memberId) {
      throw new Error('Linked Luxury Escapes account is missing')
    }

    if (!accessToken) {
      throw new Error('Linked Luxury Escapes account token is missing')
    }

    accountSetupDispatch({
      type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_CREDIT_ACCOUNT,
      businessTravellerAccount,
      creditCustomerAccessToken: accessToken,
      creditCustomerEmail: account.email,
      creditCustomerId: account.memberId,
    })
    accountSetupDispatch({
      type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO,
      step: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS.ACCOUNT_DETAILS,
    })
  }, [accountSetupDispatch])

  // Change the label of this step to match the title
  const updatedSteps = steps.map(step => {
    if (step.stepKey === 'account_details') {
      return { ...step, label: 'Link your account' }
    }
    return step
  })

  const loginMutation = useMutation({
    mutationFn: async(userDetails: Pick<App.BusinessTraveller.UserRegistrationData, 'email' | 'password'>) => {
      const reCaptchaResponse = await handleReCAPTCHA(reCaptchaRef.current)
      return login(
        { email: userDetails.email },
        userDetails.password,
        // Dedicated tenant: link the LE account without touching the LEBT session.
        // Legacy: a normal session login (matches master), consumed by the account-details step.
        isDedicatedTenantRegistrationEnabled
          ? { recaptchaData: reCaptchaResponse, brand: LUXURY_ESCAPES, includeCredentials: false }
          : { recaptchaData: reCaptchaResponse },
      )
    },
    onSuccess: (data) => {
      if (!isDedicatedTenantRegistrationEnabled) {
        // Legacy: establish the session; the effect below advances to account details once logged
        // in, covering both this password login and the normal Google login (matches master).
        dispatch({ type: API_CALL_SUCCESS, api: USER_LOGIN, data, source: 'email' })
        setIsFormSubmitting(false)
        return
      }

      try {
        continueWithCreditAccount(data.account, data.accessToken)
      } catch(error) {
        showSnackbar((error as Error).message, 'critical', { heading: 'Error linking account' })
      } finally {
        setIsFormSubmitting(false)
      }
    },
    onError: (error: App.ApiCallFailure) => {
      passwordInputRef.current?.setCustomValidity(error.message)
      formRef.current?.checkValidity()
      setIsFormSubmitting(false)
    },
  })

  const passwordOnChange = useCallback(() => {
    passwordInputRef.current?.setCustomValidity('')
    passwordInputRef.current?.reportValidity()
  }, [])

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    setIsFormSubmitting(true)
    const formData = formToObject<App.BusinessTraveller.UserRegistrationData>(e.currentTarget)
    loginMutation.mutate({
      ...formData,
      email: adminEmail,
    })
  }, [loginMutation, adminEmail])

  useEffect(() => {
    const onGoogleLinkSuccess = (event: Event) => {
      try {
        const { accessToken, account, work } = (event as CustomEvent<{ accessToken: string, account: App.AuthAccount, work: AuthAccountLoginResponse }>).detail
        if (work.account.email?.toLowerCase() !== adminEmail.toLowerCase()) throw new Error('Please use the Google account matching your signup email.')
        continueWithCreditAccount(account, accessToken, work)
      } catch(error) {
        showSnackbar((error as Error).message, 'critical', { heading: 'Error linking account' })
      } finally {
        setIsFormSubmitting(false)
      }
    }

    const onGoogleLinkFailure = (event: Event) => {
      const error = (event as CustomEvent<{ error?: Error }>).detail.error
      showSnackbar(error?.message ?? 'Google login failed', 'critical', { heading: 'Error linking account' })
      setIsFormSubmitting(false)
    }

    window.addEventListener(BUSINESS_TRAVELLER_BUSINESS_SIGNUP_GOOGLE_LINK_ACCOUNT_SUCCESS_EVENT, onGoogleLinkSuccess)
    window.addEventListener(BUSINESS_TRAVELLER_BUSINESS_SIGNUP_GOOGLE_LINK_ACCOUNT_FAILURE_EVENT, onGoogleLinkFailure)

    return () => {
      window.removeEventListener(BUSINESS_TRAVELLER_BUSINESS_SIGNUP_GOOGLE_LINK_ACCOUNT_SUCCESS_EVENT, onGoogleLinkSuccess)
      window.removeEventListener(BUSINESS_TRAVELLER_BUSINESS_SIGNUP_GOOGLE_LINK_ACCOUNT_FAILURE_EVENT, onGoogleLinkFailure)
    }
  }, [adminEmail, continueWithCreditAccount])

  useEffect(() => {
    // Legacy flow only: a normal password or Google login establishes the LEBT session, so advance
    // to account details once logged in (mirrors master). The dedicated-tenant flow never sets a
    // session here — it advances via continueWithCreditAccount instead.
    if (!isDedicatedTenantRegistrationEnabled && isLoggedIn) {
      accountSetupDispatch({
        type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO,
        step: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS.ACCOUNT_DETAILS,
      })
    }
  }, [isDedicatedTenantRegistrationEnabled, isLoggedIn, accountSetupDispatch])

  return (
    <SignupContainer>
      <LoginForm onSubmit={handleSubmission} ref={formRef}>
        {isFormSubmitting && <LoadingIndicator floating />}
        <Group direction="vertical" gap={20}>
          <Heading variant="heading3">Link your account</Heading>
          <BusinessTravellerStepper steps={updatedSteps} stepStatuses={stepStatuses} />
          <BodyText variant="medium">Business Traveller is partnered with Luxury Escapes. Connect your accounts using your Luxury Escapes password to sign in</BodyText>
          <PasswordInput
            autoComplete="new-password"
            label="Password"
            minLength={8}
            name="password"
            onChange={passwordOnChange}
            ref={passwordInputRef}
            required
            requiredErrorMessage="Please enter a password"
          />
          <TextButton
            fit="mobile-full-width"
            kind="primary"
            shape="square"
            size="medium"
            type="submit"
            disabled={isFormSubmitting}
          >
            Continue
          </TextButton>
          {isSocialMediaLoginEnabled && <>
            <Group direction="horizontal" gap={12} verticalAlign="center">
              <Divider kind="primary" />
              <BodyText variant="large">or</BodyText>
              <Divider kind="primary" />
            </Group>
            <SocialButtons
              mode="login"
              callbackSource={isDedicatedTenantRegistrationEnabled ? BUSINESS_TRAVELLER_BUSINESS_SIGNUP_GOOGLE_LINK_ACCOUNT_CALLBACK_SOURCE : undefined}
            />
          </>}
        </Group>
        {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reCaptchaRef} />}
      </LoginForm>
    </SignupContainer>
  )
}

export default BusinessTravellerSignupLinkAccount
